import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Idle timeout configuration (30 minutes in milliseconds)
  const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const idleTimerRef = React.useRef(null);

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    const userData = localStorage.getItem("user_data");
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_data");
      }
    }
    
    setLoading(false);
  }, []);

  // Auto-logout after inactivity
  useEffect(() => {
    if (!user) return; // Don't set up timer if user is not logged in

    const resetIdleTimer = () => {
      // Clear existing timer
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      // Set new timer
      idleTimerRef.current = setTimeout(() => {
        console.log("⏰ User inactive for 30 minutes, logging out...");
        logout();
      }, IDLE_TIMEOUT);
    };

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Reset timer on any activity
    events.forEach(event => {
      window.addEventListener(event, resetIdleTimer);
    });

    // Initialize timer
    resetIdleTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetIdleTimer);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [user]); // Re-run when user changes (login/logout)

  const login = async (email, password) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      console.log('🔧 Auth Context - Backend URL:', backendUrl);
      console.log('🔧 Auth Context - process.env.REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
      console.log('🔧 Auth Context - window.location.origin:', window.location.origin);
      console.log('🔧 Auth Context - Full login URL:', `${backendUrl}/api/auth/login`);
      
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Login failed");
      }

      if (data.success && data.data) {
        const { access_token, user: userData } = data.data;
        
        // Store token and user data
        localStorage.setItem("auth_token", access_token);
        localStorage.setItem("user_data", JSON.stringify(userData));
        
        setUser(userData);
        navigate("/"); // Redirect to dashboard
        
        return userData;
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_data");
    setUser(null);
    navigate("/login");
  };

  const forgotPassword = async (email) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Password reset request failed");
      }

      return data;
    } catch (error) {
      console.error("Forgot password error:", error);
      throw error;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Password reset failed");
      }

      return data;
    } catch (error) {
      console.error("Reset password error:", error);
      throw error;
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const token = localStorage.getItem("auth_token");
      
      const response = await fetch(`${backendUrl}/api/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "User update failed");
      }

      if (data.success && data.data) {
        // Update local user data if updating current user
        if (user && user.id === userId) {
          const updatedUser = { ...user, ...data.data };
          setUser(updatedUser);
          localStorage.setItem("user_data", JSON.stringify(updatedUser));
        }
      }

      return data;
    } catch (error) {
      console.error("Update user error:", error);
      throw error;
    }
  };

  const changePassword = async (userId, currentPassword, newPassword) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const token = localStorage.getItem("auth_token");
      
      const response = await fetch(`${backendUrl}/api/users/change-password/${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Password change failed");
      }

      return data;
    } catch (error) {
      console.error("Change password error:", error);
      throw error;
    }
  };

  // Initialize default admin if no admin exists
  const initializeAdmin = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/auth/initialize-admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Initialize admin error:", error);
      throw error;
    }
  };

  // Helper functions
  const isAuthenticated = () => !!user;
  const isAdmin = () => user?.role === "admin";
  const isManager = () => user?.role === "manager";
  const isAdminOrManager = () => isAdmin() || isManager();
  
  const getAuthHeader = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    user,
    loading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    updateUser,
    changePassword,
    initializeAdmin,
    isAuthenticated,
    isAdmin,
    isManager,
    isAdminOrManager,
    getAuthHeader,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};